import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plug,
  RefreshCw,
  Layers,
  Sparkles,
  Filter,
  ShieldCheck,
  FileCheck,
  TrendingUp,
  Gauge,
  Lock,
  ArrowRight,
  Palette,
  KeyRound,
  Server,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    key: "connect",
    tag: "Connect",
    title: "Bring every platform into one place",
    desc: "Securely link your CRM, ERP and finance systems and start syncing in minutes.",
    cards: [
      { icon: Plug, title: "Native connectors", desc: "Pre-built integrations for Salesforce, Zoho CRM, Zoho Books, Oracle Fusion, Oracle NetSuite, and Oracle EBS — no custom code." },
      { icon: RefreshCw, title: "Scheduled sync", desc: "Pull data on your cadence, from real-time streams to nightly batches." },
      { icon: Layers, title: "Unified schema", desc: "Normalise records from every source into one consistent, governed model." },
    ],
  },
  {
    key: "clean",
    tag: "Clean",
    title: "Trustworthy data, automatically",
    desc: "Deduplicate, validate and standardise records so your governance is built on clean data.",
    cards: [
      { icon: Filter, title: "Smart deduplication", desc: "Detect and merge duplicate records across platforms with confidence scoring." },
      { icon: FileCheck, title: "Validation rules", desc: "Enforce formats, required fields and referential integrity at ingest time." },
      { icon: RefreshCw, title: "Continuous hygiene", desc: "Ongoing cleansing keeps every record accurate and audit-ready over time." },
    ],
  },
  {
    key: "govern",
    tag: "Govern",
    title: "Policy and limits, enforced everywhere",
    desc: "Define governance once and apply it across every connected product and team.",
    cards: [
      { icon: TrendingUp, title: "Predictive limit forecasting", desc: "Know how many days until you hit a cap, not just today's usage percent." },
      { icon: Gauge, title: "Governance health score", desc: "One number summarizing risk across every connected system." },
      { icon: Lock, title: "Tamper-evident audit chain", desc: "Every extraction and rule change logged on a SHA-256 chain." },
    ],
  },
  {
    key: "ship",
    tag: "Ship",
    title: "Your brand. Your model. Your servers.",
    desc: "White-label the product, point the assistant at the LLM you already pay for, and push updates without handing over source.",
    cards: [
      { icon: Palette, title: "White-label instance", desc: "Product name, logo, colors, custom domain, and a renamed assistant. Login, emails, and the app chrome follow the same brand." },
      { icon: KeyRound, title: "Bring your own LLM", desc: "Anthropic, OpenAI, Azure, Groq, or any OpenAI-compatible endpoint including Ollama. The API key is encrypted at rest; only last-4 is shown later." },
      { icon: Server, title: "Click-to-deploy", desc: "They run private images on any VM, Kubernetes cluster, or OCI runtime — Compose, Podman, EKS, on-prem. Never a git tree. Push a release and boxes update." },
    ],
  },
];

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.07, ease: "easeOut" } }),
};

export default function Features() {
  return (
    <div data-testid="features-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Features
          </p>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Connect. Clean. Govern.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Unify CRM, ERP and finance — then ship GovernX as your product, on your model and your servers.
          </p>

          {/* Trust strip — connected platforms */}
          <div className="mt-12" data-testid="features-trust-strip">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Works with the platforms you already run
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-4">
              {["Salesforce", "Zoho CRM", "Zoho Books", "Oracle Fusion", "Oracle NetSuite", "Oracle EBS"].map(
                (name) => (
                  <span
                    key={name}
                    className="font-display text-lg font-bold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
                    data-testid={`trust-logo-${name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {name}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {SECTIONS.map((section, si) => (
        <section
          key={section.key}
          className={`py-20 ${si % 2 === 1 ? "bg-card/40" : ""}`}
          data-testid={`features-section-${section.key}`}
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mb-12 max-w-2xl">
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-primary">
                {section.tag}
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                {section.title}
              </h2>
              <p className="mt-3 text-base text-muted-foreground">{section.desc}</p>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {section.cards.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.title}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-60px" }}
                    custom={i}
                    variants={fade}
                    className="group rounded-2xl border border-border bg-background p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
                    data-testid={`feature-card-${section.key}-${i}`}
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground">{c.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <section className="border-y border-border bg-card/40 py-12" data-testid="features-deploy-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 sm:flex-row sm:items-center lg:px-8">
          <div>
            <h2 className="font-display text-2xl font-bold">Customer handbook: any server, images only</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Compose, Kubernetes, or raw OCI. They generate their own keys. HQ keys never leave Pravadh.
            </p>
          </div>
          <Link to="/deploy">
            <Button className="font-semibold" data-testid="features-deploy-guide">
              Open the deploy guide <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* EMIE Co-pilot highlight */}
      <section className="py-20" data-testid="emie-highlight">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-[#09090B] p-10 lg:p-16">
            <div className="absolute inset-0 mesh-bg opacity-80" />
            <div className="absolute inset-0 grid-lines opacity-40" />
            <div className="relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-accent">
                  <Sparkles className="h-3.5 w-3.5" /> EMIE Co-pilot
                </span>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  Your governance assistant, always on
                </h2>
                <p className="mt-4 text-base leading-relaxed text-white/80">
                  A persistent assistant across every screen. Ask why an extraction failed, when
                  you'll hit a limit, or tell it to set a rule in plain language — EMIE reasons over
                  your live data and speaks up before problems happen. On a customer
                  instance, EMIE can use their Anthropic, OpenAI, or local model instead.
                </p>
              </div>
              <Link to="/signup">
                <Button size="lg" className="h-12 gap-2 px-6 font-semibold" data-testid="emie-cta">
                  Try EMIE free <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
